<template>
  <div class="countdown-content screen-content">
    <transition-group name="fade">
      <!-- HEAD TO HEAD -->
      <div
        class="countdown-section head-to-head"
        :class="{ 'active': activeSection === 'winner' }"
        key="headToHead">
        <div class="screen-title-wrap">
          <div class="screen-title">{{ translate('headToHead') }}</div>
          <div class="screen-subtitle">
            <span>{{ translate('raceNo') }}</span>&nbsp;
            <span>{{ winnerData.raceId }}</span>
          </div>
        </div>
        <div class="screen-item-wrap"
          v-for="(competitor, idx) in winnerData.competitors"
          :class="idx === 0 ? 'left' : 'right'"
          :key="idx">
          <div class="screen-item">
            <div class="screen-item-header">
              <div class="id">{{ competitor.id }}</div>
              <div class="name">{{ competitor.name }}</div>
            </div>
            <div class="screen-item-content-top">
              <div class="title">{{ translate('raceWinner') }}</div>
              <div class="content">
                {{ competitor.odds && formatOdds(competitor.odds[winnerData.stage]) }}
              </div>
            </div>
            <div class="screen-item-content-middle">
              <div class="title">{{ translate('latestRaces') }}</div>
              <div class="content">
                <span class="content-item"
                  v-for="(result, ridx) in competitor.lastResults"
                  :class="result.won ? 'race-won' : 'race-lost'"
                  :key="ridx"
                  >&#9734;
                </span>
              </div>
            </div>
            <div class="screen-item-content-bottom">
              <div class="title">{{ translate('tournamentOutrights') }}</div>
              <div class="content">
                <div class="content-item"
                  v-if="competitor.odds.quarterFinals">
                  <div>{{ translate('semiFinals') }}</div>
                  <div class="content-item-value">
                    {{ formatOdds(competitor.odds.quarterFinals) }}
                  </div>
                </div>
                <div class="content-item"
                  v-if="competitor.odds.semiFinals">
                  <div>{{ translate('finals') }}</div>
                  <div class="content-item-value">
                    {{ formatOdds(competitor.odds.semiFinals) }}
                  </div>
                </div>
                <div class="content-item"
                  v-if="competitor.odds.finals">
                  <div>{{ translate('tournament') }}</div>
                  <div class="content-item-value">
                    {{ formatOdds(competitor.odds.finals) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="screen-item-image" :class="`racer-${competitor.id}`"></div>
        </div>
      </div>
      <!-- RACE SCHEDULE -->
      <div
        class="countdown-section race-schedule"
        :class="{ 'active': activeSection === 'schedule' }"
        key="raceSchedule">
        <div class="section-content">
          <div class="screen-title-wrap">
            <div class="screen-title">{{ translate('raceSchedule') }}</div>
            <div class="screen-subtitle">
              <span>{{ translate('tournamentNo') }}</span>&nbsp;
              <span>{{ tournamentId }}</span>
            </div>
          </div>
          <div class="races-list-wrap">
            <ul class="races-list left"
              v-if="scheduleData.left.length">
              <li
                v-for="(race, idx) in scheduleData.left"
                :key="`race-${idx}`"
                class="race-item">
                <div class="race-start-time">
                  {{ idx === 0 ? translate('raceNow') : translate(race.tournamentStage) }}
                </div>
                <div class="race-details">
                  <div class="item-cell racer-id racer-1-id">
                    {{ race.competitor1.id }}
                  </div>
                  <div class="item-cell racer-name racer-1-name">
                    {{ race.competitor1.name }}
                  </div>
                  <div class="item-cell">VS</div>
                  <div class="item-cell racer-name racer-2-name">
                    {{ race.competitor2.name }}
                  </div>
                  <div class="item-cell racer-id racer-2-id">
                    {{ race.competitor2.id }}
                  </div>
                </div>
                <div class="race-odds">
                  <div class="item-cell"></div>
                  <div class="item-cell racer-odds racer-1-odds">
                    {{ formatOdds(race.competitor1.odds) }}
                  </div>
                  <div class="item-cell"></div>
                  <div class="item-cell racer-odds racer-2-odds">
                    {{ formatOdds(race.competitor2.odds) }}
                  </div>
                  <div class="item-cell"></div>
                </div>
              </li>
            </ul>
            <ul class="races-list right"
              v-if="scheduleData.right.length">
              <li
                v-for="(race, idx) in scheduleData.right"
                :key="`race-${idx}`"
                class="race-item">
                <div class="race-start-time">
                  {{ translate(race.tournamentStage) }}
                </div>
                <div class="race-details">
                  <div class="item-cell racer-id racer-1-id">
                    {{ race.competitor1.id }}
                  </div>
                  <div class="item-cell racer-name racer-1-name">
                    {{ race.competitor1.name }}
                  </div>
                  <div class="item-cell">VS</div>
                  <div class="item-cell racer-name racer-2-name">
                    {{ race.competitor2.name }}
                  </div>
                  <div class="item-cell racer-id racer-2-id">
                    {{ race.competitor2.id }}
                  </div>
                </div>
                <div class="race-odds">
                  <div class="item-cell"></div>
                  <div class="item-cell racer-odds racer-1-odds">
                    {{ formatOdds(race.competitor1.odds) }}
                  </div>
                  <div class="item-cell"></div>
                  <div class="item-cell racer-odds racer-2-odds">
                    {{ formatOdds(race.competitor2.odds) }}
                  </div>
                  <div class="item-cell"></div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <!-- OUTRIGHTS -->
      <div
        class="countdown-section outrights"
        :class="{ 'active': activeSection === 'outrights' }"
        key="outrights">
        <div class="section-content">
          <div class="screen-title-wrap">
            <div class="screen-title"> {{ translate('outrights') }}</div>
            <div class="screen-subtitle">
              <span>{{ translate('tournamentNo') }}</span>&nbsp;
              <span>{{ tournamentId }}</span>
            </div>
          </div>
          <div class="racers-list-wrap">
            <ul class="racers-list left"
              v-if="outrightsData.left.length">
              <li class="racers-list-caption">
                <div class="item-cell"></div>
                <div class="item-cell"></div>
                <div class="item-cell">{{ translate('semiFinals') }}</div>
                <div class="item-cell">{{ translate('finals') }}</div>
                <div class="item-cell">{{ translate('tournament') }}</div>
              </li>
              <li
                v-for="(racer, idx) in outrightsData.left"
                :key="`racer-${idx}`"
                class="racers-list-item"
                :class="{ 'racer-out': !racer.odds }"
                >
                <div class="item-cell">{{ racer.id }}</div>
                <div class="item-cell">{{ racer.name }}</div>
                <template v-if="racer.odds !== null">
                  <div class="item-cell">{{ formatOdds(racer.odds.quarterFinals) }}</div>
                  <div class="item-cell">{{ formatOdds(racer.odds.semiFinals) }}</div>
                  <div class="item-cell">{{ formatOdds(racer.odds.finals) }}</div>
                </template>
                <template v-else>
                  <div class="item-cell emphsize">{{ translate('racerOut') }}</div>
                </template>
              </li>
            </ul>
            <ul class="racers-list right"
              v-if="outrightsData.right.length">
              <li class="racers-list-caption">
                <div class="item-cell"></div>
                <div class="item-cell"></div>
                <div class="item-cell">{{ translate('semiFinals') }}</div>
                <div class="item-cell">{{ translate('finals') }}</div>
                <div class="item-cell">{{ translate('tournament') }}</div>
              </li>
              <li
                v-for="(racer, idx) in outrightsData.right"
                :key="`racer-${idx}`"
                class="racers-list-item"
                :class="{ 'racer-out': !racer.odds }"
                >
                <div class="item-cell">{{ racer.id }}</div>
                <div class="item-cell">{{ racer.name }}</div>
                <template v-if="racer.odds !== null">
                  <div class="item-cell">{{ formatOdds(racer.odds.quarterFinals) }}</div>
                  <div class="item-cell">{{ formatOdds(racer.odds.semiFinals) }}</div>
                  <div class="item-cell">{{ formatOdds(racer.odds.finals) }}</div>
                </template>
                <template v-else>
                  <div class="item-cell emphsize">{{ translate('racerOut') }}</div>
                </template>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { isNil } from 'lodash';
import { mapGetters } from 'vuex';
import { formatNumber, translate } from '@nsftx/games-visualization-sdk/src/utility';

export default {
  name: 'StageCountdown',
  mixins: [],
  components: {},
  data() {
    return {
      changeScreenIntervel: null,
      changeScreenDuration: null,
      activeSection: null,
      sections: [
        'winner',
        'schedule',
        'outrights',
      ],
      winnerData: {},
      scheduleData: {},
      outrightsData: {},
    };
  },

  computed: {
    ...mapGetters([
      'tournamentId',
      'game',
      'ui',
      'countdownTime',
      'lastCountdownTime',
    ]),
    showcaseTime() {
      let time = 20;
      if (this.countdownTime > this.lastCountdownTime) {
        time = this.countdownTime - this.lastCountdownTime;
      }
      return time;
    },
  },

  methods: {
    setActiveScreen(scrn) {
      this.activeSection = this.sections[scrn] || this.sections[0];
    },
    shiftScreens() {
      const self = this;
      this.changeScreenDuration = this.ui.showcaseItemTime;

      let cntr = 0;

      this.stopScreenShift();

      if (this.showcaseTime > this.ui.showcaseItemTime) {
        // if (!this.ui.showcaseLoop) {
        //   this.changeScreenDuration = this.showcaseTime / this.sections.length || 10;
        // }

        self.changeScreenIntervel = setInterval(() => {
          cntr += 1;
          if (!this.sections[cntr]) {
            if (this.ui.showcaseLoop) {
              cntr = 0;
            } else {
              self.stopScreenShift();
            }
          }
          self.setActiveScreen(cntr);
        }, this.changeScreenDuration * 1000);
      }

      self.setActiveScreen(0);
    },
    stopScreenShift() {
      clearInterval(this.changeScreenIntervel);
      this.changeScreenIntervel = null;
    },
    formatWinnerData() {
      this.winnerData.raceId = this.game.raceId;
      this.winnerData.stage = this.game.offer.nextEvent.stage;
      this.winnerData.competitors = [
        this.game.offer.nextEvent.competitor1,
        this.game.offer.nextEvent.competitor2,
      ];
    },
    formatScheduleData() {
      const { schedule } = this.game.offer;
      this.scheduleData.left = [];
      this.scheduleData.right = [];

      Object.keys(schedule).forEach((stage) => {
        const stageRaces = this.game.offer.schedule[stage];

        stageRaces.forEach((race) => {
          const item = race;
          const container = this.scheduleData.left.length < 4
            ? 'left' : 'right';
          item.tournamentStage = stage;
          this.scheduleData[container].push(item);
        });
      });
    },
    formatOutrightsData() {
      const outrights = this.game.offer.competitors.sort(this.compareIds);
      this.outrightsData.left = [];
      this.outrightsData.right = [];
      outrights.forEach((competitor) => {
        const item = competitor;
        const container = this.outrightsData.left.length < 8
          ? 'left' : 'right';
        this.outrightsData[container].push(item);
      });
    },
    formatOdds(odds) {
      return !isNil(odds) ? formatNumber(odds) : '-';
    },
    compareIds(a, b) {
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return 0;
    },
    translate(key) {
      return translate(key);
    },
  },

  created() {
    this.formatWinnerData();
    this.formatScheduleData();
    this.formatOutrightsData();
  },

  mounted() {
    setTimeout(this.shiftScreens, 2500);
  },

  beforeDestroy() {
    this.stopScreenShift();
  },
};
</script>

<style lang="scss">
.countdown-section {
  opacity: 0;
  transition: opacity .5s;

  &.active {
    opacity: 1;
  }
}
.screen-item-wrap {
  position: absolute;
  top: 0;
  width: 31vw;
  height: 100%;

  &.left {
    left: 2vw;
  }
  &.right {
    right: 2vw;
  }
}
.screen-item {
  position: relative;
  width: 100%;
  height: 100%;
  -webkit-backdrop-filter: blur(0);
  backdrop-filter: blur(0);
  background-color: rgba($primary-bg-color, 0.2);
  font-size: get-vw(16px);
  color: $secondary-text-color;
  text-align: center;
  text-transform: uppercase;
  transition: all .5s ease-out, opacity 1.5s;
  opacity: 0;
  overflow: hidden;
}
.left .screen-item {
  transform: translateX(-35%) translateZ(0);
}
.right .screen-item {
  transform: translateX(35%) translateZ(0);
}
.active {
  .screen-item {
    -webkit-backdrop-filter: blur(7px);
    backdrop-filter: blur(7px);
    opacity: 1;
    transform: translateX(0%) translateZ(0);
    transition-delay: .5s;
  }
  .right .screen-item {
    transition-delay: .8s;
  }
}

.screen-item-content-middle {

  .content-item {
    position: relative;
    font-size: get-vw(34px);
    color: $highlight-color;

    &.race-won:after {
      content: "\2605";
      position: absolute;
      top: 0;
      left: 0;
    }
  }
}

.screen-item-image {
  position: absolute;
  bottom: 20vh;
  width: get-vw(501px);
  height: get-vw(213px);
  background: {
    repeat: no-repeat;
    position: 0 -5%;
    size: cover;
  };

  @for $i from 1 through 20 {
    &.racer-#{$i} {
      background-image: url('../assets/h2h/racer-#{$i}.png');
    }
  }
}

.active .screen-item-image {
  animation: verticalSprite 1.3s steps(36) forwards;
}

.left .screen-item-image {
  right: -40%;
}

.right .screen-item-image {
  transform: scaleX(-1) translateZ(0);
  animation-delay: .25s;
  left: -40%;
}

// RACE SCHEDULE SECTION
.races-list-wrap {
  position: relative;
  top: 19vh;
  width: 70vw;
  height: auto;
  margin: 0 auto;
  text-align: center;
  color: $primary-text-color;
}

.races-list {
  display: inline-block;
  width: 48%;
  height: 100%;
  margin: 0 1vw;
  vertical-align: top;
}

.race-item {
  width: 100%;
  height: auto;
  margin-bottom: 1.2vh;
}

.race-start-time {
  width: 100%;
  height: 4vh;
  line-height: 5vh;
  text-align: left;
  text-transform: uppercase;
}

.right .race-start-time {
  text-align: right;
}

.race-details,
.race-odds {
  display: table;
  width: 100%;
  height: get-vh(36px);
  background: $highlight-color;

  .item-cell {
    display: table-cell;
    vertical-align: middle;

    &:nth-child(1),
    &:nth-child(5) {
      width: get-vw(45px);
    }

    &:nth-child(3) {
      opacity: .6;
    }

    &:nth-child(2),
    &:nth-child(4) {
      width: 30%;
    }
  }

  .racer-id {
    font-weight: 700;
  }
}

.race-odds {
  height: get-vh(48px);
  background: rgba($primary-bg-color, .6);
  font-size: get-vh(32px);
}

// OUTRIGHTS SECTION
.racers-list-wrap {
  position: relative;
  top: 25vh;
  width: 82vw;
  height: auto;
  margin: 0 auto;
  text-align: center;
}

.racers-list {
  display: inline-block;
  width: 48.5%;
  height: 100%;

  &.left {
    float: left;
  }

  &.right {
    float: right;
  }
}
.racers-list-caption,
.racers-list-item {
  display: table;
  table-layout: fixed;
  width: 100%;
  height: get-vh(40px);

  .item-cell {
    display: table-cell;
    vertical-align: middle;
    font-size: get-vw(14px);
    text-transform: capitalize;
    overflow: hidden;

    &:nth-child(1) {
      width: get-vw(40px);
    }

    &:nth-child(2) {
      position: relative;
      padding-left: .5vw;
      text-align: left;
    }

    &:nth-child(3),
    &:nth-child(4),
    &:nth-child(5) {
      width: 18%;
    }

    &.emphsize {
      text-transform: uppercase;
    }
  }
}
.racers-list-caption {
  height: get-vw(25px);
  color: $secondary-text-color;
}
.racers-list-item {
  margin-bottom: 1.25vh;
  background: rgba(0, 9, 0, .5);
  color: $primary-text-color;

  &.racer-out {
    opacity: .4;
  }

  .item-cell {
    font-size: get-vw(18px);

    &:nth-child(1) {
      background: $highlight-color;
    }
  }
}

.stream-runner-mode {

  .screen-item-wrap {
    width: 33vw;

    &.left {
      left: 1vw;
    }
    &.right {
      right: 1vw;
    }
  }

  .left .screen-item-image {
    right: -30%;
  }

  .right .screen-item-image {
    left: -30%;
  }

  .racers-list-wrap {
    top: 20vh;
    width: 90vw;
  }

  .racers-list {
    width: 49%;
  }

  .racers-list-caption {
    height: get-vw(35px);
    margin-bottom: 0;

    .item-cell {
      font-size: get-vw(18px);
    }
  }

  .racers-list-item {
    height: get-vw(45px);
    margin-bottom: get-vh(8px);

    .item-cell {
      font-size: get-vw(22px);
    }
  }

  .races-list-wrap {
    top: 18vh;
    width: 85vw;
  }

  .race-details,
  .race-odds {
    height: get-vh(40px);
    font-size: get-vw(20px);
  }

  .race-odds {
    height: get-vh(50px);
    font-size: get-vw(28px);
  }

  .race-start-time {
    height: 4.3vh;
    font-size: get-vw(18px);
  }
}
</style>
